import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { Button, Form, Toast, Upload } from "@douyinfe/semi-ui";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@state/store";
import { IconSearch } from "@douyinfe/semi-icons";
import { ReactComponent as ArrowIcon } from "@assets/icons/arrow_icon.svg";
import {
  ANTIBODY_DESIGN_TYPE_MAP,
  PEPTIDE_DESIGN_TYPE_MAP,
} from "@services/home/config/home.config";
import {
  AntibodyDesignBinderTypeEnum,
  BinderDesignTypeEnum,
  JobTypeEnum,
  PeptideDesignBinderTypeEnum,
  VhhDesignTypeEnum,
} from "@services/home/enum/home.enum";
import { getLocalStorage, setLocalStorage } from "@utils/localstorage";
import {
  JobPayloadModel,
  ProteinBinderDesignPayloadModel,
  TokenInfoModel,
  VhhDesignAttachmentModel,
  DockingAttachementModel,
  DockingPayloadModel,
  SequenceRowModel,
  JobModel,
  VhhDesignPayloadModel,
  ResidueMapModel,
  JobSubmitInfoModel,
  PeptideDesignPayloadModel,
  ExampleModel,
  ExtraInfoModel,
} from "@services/home/model/home.model";
import { Page } from "@services/common/model/page.model";
import { AxiosError } from "axios";
import { convertFile } from "@utils/file.util";
import {
  createProteinBinderDesignApi,
  getProteinBinderDesignTokenInfoApi,
  getFoldSequenceStructureApi,
  getExamplesApi,
  getJobDetailApi,
  checkLigandCCDApi,
} from "@services/home/home.api";
import { batchGetFileInfoApi } from "@services/common/common.api";
import { setSelectedMenu, setIsJobSubmited } from "@state/global/reducer";
import { getUserInfoApi } from "@services/user/user.api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sleep } from "@utils/common.util";
import {
  checkRna,
  checkDna,
  checkLigandSmiles,
  checkPdbId,
  checkAlphafoldId,
} from "@utils/data-process.util";
import {
  downloadPdbFileApi,
  downloadAlphafoldApi,
} from "@services/task/task.api";
import { isEqual } from "lodash";
import { antibodyChainValidate } from "@utils/validator";
import { replaceWhitespace } from "@utils/common.util";
import ProteinBinderDesignAttachmentField from "../components/protein-binder-design-attachment-field";
import VhhDesignAttachmentField from "../components/vhh-design-attachment-field";
import JobLoginDialog from "../components/job-login-dialog";
import ProteinBinderAdvancedField from "../components/protein-binder-advanced-field";
import ContactFooter from "@layouts/contact-footer";
import DockingTableField from "../components/docking-table-field";
import MainTextarea from "../components/main-textarea";
import styles from "./index.module.scss";
import ProteinBinderDesignTableField from "../components/protein-binder-design-table-field";
import JobTypeCascader from "../components/job-type-cascader";
import { useTranslation, Trans } from "react-i18next";

const Home = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const divRef = useRef<HTMLDivElement>(null);
  const dockingTableRef = useRef<{
    setupGuide(sequences: SequenceRowModel[]): void;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const jobType = searchParams.get("job_type") as JobTypeEnum;
  const jobId = searchParams.get("job_id");
  const formRef = useRef<FormApi<JobPayloadModel>>();
  const [loginVisible, setLoginVisible] = useState(false);
  const [jobParameter, setJobParameter] = useState<JobPayloadModel | null>(
    null,
  );
  const [userType, setUserType] = useState("");

  const [tokenInfo, setTokenInfo] = useState<TokenInfoModel>();

  const [username, setUsername] = useState<string>();

  const [loading, setLoading] = useState(false);

  const [loadingInfo, setLoadingInfo] = useState(false);

  const [submitingJob, setSubmitingJob] = useState(false);

  const [selectedJobType, setSelectedJobType] = useState(
    jobType || "antibody_design_lite-vhh",
  );

  const [exampleList, setExampleList] = useState<ExampleModel[]>([]);

  const [exampleJobs, setExampleJobs] = useState<JobModel[]>([]);

  const [exampleJobDetail, setExampleJobDetail] = useState<JobModel>();

  const [tutorialVisible, setTutorialVisible] = useState(false);

  const [contentHeight, setContentHeight] = useState(0);

  const [isBinderDesignAdvanced, setIsBinderDesignAdvanced] = useState(false);

  const [extraInfo, setExtraInfo] = useState<ExtraInfoModel | undefined>(
    undefined,
  );

  const uploadRef = useRef<Upload>(null);

  const getPepideLabel = (type: PeptideDesignBinderTypeEnum): string =>
    t(`peptideDesignType.${type}`);

  function onUpload() {
    setTimeout(() => {
      uploadRef.current?.onClick();
    }, 0);
  }

  function getProteinBinderValues(p: JobPayloadModel, name: string) {
    const params: ProteinBinderDesignPayloadModel = {
      job_name: name,
    };

    const attachment = p.protein_binder_attachment;

    const advanced = p.protein_advanced;

    if (attachment && advanced) {
      params.geo_flow_lite = {
        mode: "easy",
        target_type: attachment.target_type,
        deepthink: attachment.deepthink,
        target_sequence: p.input_text,
        target_copies: advanced.target_copies,
        binder_length: advanced.binder_length,
        binder_copies: advanced.binder_copies,
      };
    }

    return params;
  }

  async function getFile(
    type: VhhDesignTypeEnum,
    id: string,
    deepthink: boolean,
  ) {
    let p: Promise<File | null> | null = null;
    if (type === VhhDesignTypeEnum.PdbID) {
      const msg = checkPdbId(id);
      if (msg) {
        Toast.error(msg);
        return;
      }
      p = downloadPdbFileApi(id);
    } else if (type === VhhDesignTypeEnum.UniProtID) {
      const msg = checkAlphafoldId(id);
      if (msg) {
        Toast.error(msg);
        return;
      }
      p = downloadAlphafoldApi(id);
    } else {
      const msg = antibodyChainValidate(id);
      if (msg) {
        Toast.error(msg);
        return;
      }

      p = getFoldSequenceStructureApi(id);
    }

    try {
      setLoading(true);

      const file = await p;
      setLoading(false);

      if (!file) {
        let msg = "";

        if (type === VhhDesignTypeEnum.PdbID) {
          msg = `Invalid PDB ID "${id}"`;
        } else if (type === VhhDesignTypeEnum.UniProtID) {
          msg = `Invalid UniProt ID "${id}"`;
        } else if (type === VhhDesignTypeEnum.AntigenSequence) {
          msg = `Invalid sequence`;
        }
        Toast.error(msg);

        return;
      }

      const blobUrl = URL.createObjectURL(file);

      if (selectedJobType.startsWith(JobTypeEnum.VhhDesign)) {
        navigate(
          `/job?fileUrl=${encodeURIComponent(blobUrl)}&filename=${
            file.name
          }&deepthink=${
            deepthink ? "Yes" : "No"
          }&protein_type=${type}&input_content=${id}&job_type=${selectedJobType}`,
        );
      } else if (selectedJobType.startsWith(JobTypeEnum.PeptideDesignLite)) {
        navigate(
          `/peptide-job?fileUrl=${encodeURIComponent(blobUrl)}&filename=${
            file.name
          }&deepthink=${
            deepthink ? "Yes" : "No"
          }&protein_type=${type}&input_content=${id}&job_type=${selectedJobType}`,
        );
      }
    } catch (e) {
      if (e instanceof Error) {
        Toast.error(e.message);
      }

      setLoading(false);
    }
  }

  function getTokenInfo(p: JobPayloadModel) {
    setLoadingInfo(true);
    const params = getProteinBinderValues(p, "");

    getProteinBinderDesignTokenInfoApi(params)
      .then((data) => {
        setTokenInfo(data);
        setLoadingInfo(false);
      })
      .catch(() => {
        setTokenInfo(undefined);
        setLoadingInfo(false);
      });
  }

  async function checkProteinBinderJob(p: JobPayloadModel) {
    const inputText = replaceWhitespace(p.input_text);
    if (!inputText) {
      Toast.error("Input something to start");
      return;
    }
    const binderAttachment = p.protein_binder_attachment;
    if (!binderAttachment) {
      return;
    }
    const targetType = binderAttachment.target_type;

    try {
      if (targetType === BinderDesignTypeEnum.Ligand) {
        const msg = await checkLigandSmiles(inputText);
        if (msg) {
          Toast.error(msg);
          return;
        }
      } else if (targetType === BinderDesignTypeEnum.LigandCCD) {
        const { code, message } = await checkLigandCCDApi([inputText]);

        if (code !== 200) {
          Toast.error(message);
          return;
        }
      } else if (targetType === BinderDesignTypeEnum.RnaSequence) {
        const msg = checkRna(inputText);
        if (msg) {
          Toast.error(msg);
          return;
        }
      } else if (targetType === BinderDesignTypeEnum.DnaSequenceDs) {
        const msg = checkDna(inputText);
        if (msg) {
          Toast.error(msg);
          return;
        }
      } else if (targetType === BinderDesignTypeEnum.ProteinChain) {
        const msg = antibodyChainValidate(inputText);
        if (msg) {
          Toast.error(msg);
          return;
        }
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        const error = e as AxiosError<Page<null>>;
        const { response } = error;
        if (response) {
          Toast.error(response.data.message);
        }
      }
      console.error(e);
      return;
    }

    try {
      const { code, data } = await getUserInfoApi();

      if (code === 200) {
        setUserType("registered");
        setUsername(data.username);
        getTokenInfo(p);
      } else {
        setUserType("anonymous");
        setUsername(undefined);
      }

      setLoginVisible(true);
    } catch (e) {
      setUserType("anonymous");
      setUsername(undefined);

      setLoginVisible(true);
    }
  }

  function onSubmit(p: JobPayloadModel) {
    setJobParameter(p);

    if (p.job_type === JobTypeEnum.ProteinBinderDesign) {
      const params = getProteinBinderValues(p, "");

      const targetJob = exampleJobs.find((item) => {
        const isSame = isEqual(
          params.geo_flow_lite,
          (item.parameters as ProteinBinderDesignPayloadModel)?.geo_flow_lite,
        );

        return isSame;
      });

      if (targetJob) {
        navigate(`/job-detail/${targetJob.id}`);

        const jobSubmitedInfo =
          getLocalStorage<JobSubmitInfoModel>("is_job_submited");
        if (!jobSubmitedInfo) {
          dispatch(setIsJobSubmited(true));
          setLocalStorage("is_job_submited", {
            submited: true,
          } as JobSubmitInfoModel);
        }

        return;
      }

      checkProteinBinderJob(p);
    } else if (p.job_type.startsWith(JobTypeEnum.VhhDesign)) {
      const inputText = replaceWhitespace(p.input_text);
      if (!inputText) {
        Toast.error("Input something to start");
        return;
      }

      if (exampleJobDetail) {
        navigate(`/job?job_id=${exampleJobDetail.id}`);
        const jobSubmitedInfo =
          getLocalStorage<JobSubmitInfoModel>("is_job_submited");
        if (!jobSubmitedInfo) {
          dispatch(setIsJobSubmited(true));
          setLocalStorage("is_job_submited", {
            submited: true,
          } as JobSubmitInfoModel);
        }
        return;
      }

      if (p.vhh_design_attachment) {
        getFile(
          p.vhh_design_attachment.protein_type,
          inputText,
          p.vhh_design_attachment.deep_think,
        );
      }
    } else if (p.job_type.startsWith(JobTypeEnum.PeptideDesignLite)) {
      const inputText = replaceWhitespace(p.input_text);
      if (!inputText) {
        Toast.error("Input something to start");
        return;
      }

      if (p.vhh_design_attachment) {
        getFile(
          p.vhh_design_attachment.protein_type,
          inputText,
          p.vhh_design_attachment.deep_think,
        );
      }
    }
  }

  function onLogined() {
    if (!jobParameter) {
      return;
    }
    getTokenInfo(jobParameter);
  }

  function onLoginCancel() {
    setLoginVisible(false);
  }

  function onLoginSubmit(job_name?: string) {
    if (!jobParameter || !job_name) {
      return;
    }

    if (jobParameter.job_type === JobTypeEnum.ProteinBinderDesign) {
      const params = getProteinBinderValues(jobParameter, job_name);

      setSubmitingJob(true);
      createProteinBinderDesignApi(params)
        .then(({ code, data, message }) => {
          if (code === 200) {
            Toast.success(message);
            navigate(`/job-detail/${data.job_id}`);
            const jobSubmitedInfo =
              getLocalStorage<JobSubmitInfoModel>("is_job_submited");
            if (!jobSubmitedInfo) {
              dispatch(setIsJobSubmited(true));
              setLocalStorage("is_job_submited", {
                submited: true,
              } as JobSubmitInfoModel);
            }
            onLoginCancel();
          }

          setSubmitingJob(false);
        })
        .catch((e) => {
          setSubmitingJob(false);
          console.error(e);
        });
    }
  }

  function onVhhFiledChange(p: VhhDesignAttachmentModel) {
    if (p.protein_type === VhhDesignTypeEnum.UploadStructure) {
      onUpload();
    }
  }

  async function transformPdbFile(file: File) {
    const fileName = file.name.toLowerCase();

    const attachment = jobParameter?.vhh_design_attachment;

    if (fileName.endsWith(".pdb")) {
      file = await convertFile(file);
    }

    if (attachment) {
      const blobUrl = URL.createObjectURL(file);

      if (selectedJobType.startsWith(JobTypeEnum.VhhDesign)) {
        navigate(
          `/job?fileUrl=${encodeURIComponent(blobUrl)}&filename=${
            file.name
          }&deepthink=${attachment.deep_think ? "Yes" : "No"}&protein_type=${
            attachment.protein_type
          }&job_type=${selectedJobType}`,
        );
      } else if (selectedJobType.startsWith(JobTypeEnum.PeptideDesignLite)) {
        navigate(
          `/peptide-job?fileUrl=${encodeURIComponent(blobUrl)}&filename=${
            file.name
          }&deepthink=${attachment.deep_think ? "Yes" : "No"}&protein_type=${
            attachment.protein_type
          }&job_type=${selectedJobType}`,
        );
      }
    }
  }

  function onFileChange(fileList: File[]) {
    const [file] = fileList;

    transformPdbFile(file);
  }

  function onAcceptInvalid() {
    Toast.error(`Invalid file suffix: expected ".cif" or ".pdb"`);
  }

  function onTextSubmit(v: string) {
    if (v && jobParameter) {
      onSubmit({
        ...jobParameter,
        input_text: v,
      });
    }
  }

  function onFormValuesChange(values: JobPayloadModel) {
    setJobParameter(values);
    setSelectedJobType(values.job_type);
  }

  async function getExamples(type: JobTypeEnum, binder_type?: string) {
    try {
      const { code, data } = await getExamplesApi(type, binder_type);
      if (code === 200) {
        setExampleList(data.list);

        const promises = data.list.map((item) => {
          return getJobDetailApi(item.id);
        });

        const examples = await Promise.all(promises);
        setExampleJobs(examples);
      } else {
        setExampleList([]);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        const error = e as AxiosError<null>;
        if (error.response && error.response.status !== 401) {
          setExampleList([]);
        }
      }
    }
  }

  async function onGetJobDetail(jid: string) {
    if (!formRef.current) {
      return;
    }
    try {
      const data = await getJobDetailApi(jid);
      if (data.type_name === JobTypeEnum.VhhDesign) {
        const params = data.parameters as VhhDesignPayloadModel;

        formRef.current?.setValue(
          "job_type",
          `${data.type_name}-${params.binder_type}`,
        );
      } else if (data.type_name === JobTypeEnum.PeptideDesignLite) {
        const params = data.parameters as PeptideDesignPayloadModel;

        formRef.current?.setValue(
          "job_type",
          `${data.type_name}-${params.binder_type}`,
        );
      } else {
        formRef.current?.setValue("job_type", data.type_name);
      }
      setExampleJobDetail(data);

      await sleep(100);

      dispatch(
        setSelectedMenu({
          job_type: data.type_name,
        }),
      );

      if (data.type_name === JobTypeEnum.ProteinBinderDesign) {
        const params = data.parameters as ProteinBinderDesignPayloadModel;

        const geoFlowLite = params.geo_flow_lite;

        if (geoFlowLite) {
          if (geoFlowLite.mode && geoFlowLite.mode === "multi_entity") {
            setIsBinderDesignAdvanced(true);

            const p: JobPayloadModel = {
              job_type: data.type_name,
              input_text: "",
              advanced_protein_binder_attachment: {
                deepthink: geoFlowLite.deepthink,
                entity_list: geoFlowLite.entity_list!,
              },
            };

            setTimeout(() => {
              formRef.current?.setValues(p);
            }, 10);
          } else {
            setIsBinderDesignAdvanced(false);
            const p: JobPayloadModel = {
              job_type: data.type_name,
              input_text: geoFlowLite.target_sequence!,
              protein_binder_attachment: {
                target_type: geoFlowLite.target_type!,
                deepthink: geoFlowLite.deepthink,
              },
              protein_advanced: {
                target_copies: geoFlowLite.target_copies,
                binder_length: geoFlowLite.binder_length,
                binder_copies: geoFlowLite.binder_copies,
              },
            };

            setTimeout(() => {
              formRef.current?.setValues(p);
            }, 10);
          }
        }
      } else if (data.type_name === JobTypeEnum.VhhDesign) {
        setTutorialVisible(true);

        const params = data.parameters as VhhDesignPayloadModel;
        const feInfo = JSON.parse(params.fe_info || "{}") as ResidueMapModel;

        const files = await batchGetFileInfoApi(
          [params.antigen_file_id],
          "base",
        );

        const [file] = files;

        if (feInfo.protein_type) {
          const vhhDesignAttachment = formRef.current.getValue(
            "vhh_design_attachment",
          );

          if (vhhDesignAttachment) {
            formRef.current.setValue("vhh_design_attachment", {
              ...vhhDesignAttachment,
              protein_type: feInfo.protein_type,
            });
          }

          if (feInfo.input_content) {
            formRef.current.setValue("input_text", feInfo.input_content);
          }
        } else {
          const name = file.name.split(".").shift();

          if (name) {
            formRef.current.setValue("input_text", name);
          }
        }
      } else if (data.type_name === JobTypeEnum.AbAgDocking) {
        const params = data.parameters as DockingPayloadModel;
        const seqs = params.sequences.map((item) => {
          const temp: SequenceRowModel = {
            copies: item.chain_ids.length,
            sequence: item.sequence,
            struct_condition_fid: item.struct_condition_fid,
            struct_condition_items: item.struct_condition_items,
            pdb_input_type: item.pdb_input_type,
            pdb_input_value: item.pdb_input_value,
            isEdit: false,
          };

          return temp;
        });
        const o: DockingAttachementModel = {
          sequences: seqs,
          constraints: params.constraints,
          turbo: params.turbo,
        };

        formRef.current?.setValue("docking_attachment", o);

        setTimeout(() => {
          dockingTableRef.current?.setupGuide(o.sequences);
        }, 100);
      } else if (data.type_name === JobTypeEnum.PeptideDesignLite) {
        setTutorialVisible(true);

        const params = data.parameters as PeptideDesignPayloadModel;
        const feInfo = JSON.parse(params.fe_info || "{}") as ResidueMapModel;

        const files = await batchGetFileInfoApi(
          [params.antigen_file_id],
          "base",
        );

        const [file] = files;

        if (feInfo.protein_type) {
          const vhhDesignAttachment = formRef.current.getValue(
            "vhh_design_attachment",
          );

          if (vhhDesignAttachment) {
            formRef.current.setValue("vhh_design_attachment", {
              ...vhhDesignAttachment,
              protein_type: feInfo.protein_type,
            });
          }

          if (feInfo.input_content) {
            formRef.current.setValue("input_text", feInfo.input_content);
          }
        } else {
          const name = file.name.split(".").shift();

          if (name) {
            formRef.current.setValue("input_text", name);
          }
        }
      }
    } catch (e) {
      console.error("onGetJobDetail-->", e);
    }
  }

  function exampleClick(item: ExampleModel) {
    onGetJobDetail(item.id);
    setExtraInfo(item.extra_info);
  }

  function onSkip() {
    if (!exampleJobDetail) {
      return;
    }

    if (exampleJobDetail.type_name === JobTypeEnum.VhhDesign) {
      const params = exampleJobDetail.parameters as VhhDesignPayloadModel;
      navigate(
        `/job?job_id=${exampleJobDetail.id}&job_type=${exampleJobDetail.type_name}-${params.binder_type}`,
      );
    } else if (exampleJobDetail.type_name === JobTypeEnum.PeptideDesignLite) {
      const params = exampleJobDetail.parameters as PeptideDesignPayloadModel;
      navigate(
        `/peptide-job?job_id=${exampleJobDetail.id}&job_type=${exampleJobDetail.type_name}-${params.binder_type}`,
      );
    }
  }

  function onNext() {
    if (!exampleJobDetail) {
      return;
    }

    if (exampleJobDetail.type_name === JobTypeEnum.VhhDesign) {
      const params = exampleJobDetail.parameters as VhhDesignPayloadModel;
      navigate(
        `/job?job_id=${exampleJobDetail.id}&job_type=${exampleJobDetail.type_name}-${params.binder_type}&mode=tuition`,
      );
    } else if (exampleJobDetail.type_name === JobTypeEnum.PeptideDesignLite) {
      const params = exampleJobDetail.parameters as PeptideDesignPayloadModel;
      navigate(
        `/peptide-job?job_id=${exampleJobDetail.id}&job_type=${exampleJobDetail.type_name}-${params.binder_type}&mode=tuition`,
      );
    }
  }

  function onJobTypeChange(v: string) {
    setSearchParams({
      job_type: v,
    });

    setExampleList([]);
    setIsBinderDesignAdvanced(false);
  }

  function onTryAdvanced() {
    setIsBinderDesignAdvanced(true);
  }

  function onSwitchSimpleMode() {
    setIsBinderDesignAdvanced(false);
  }

  function toPromotion() {
    window.open("/promotion", "_blank");
  }

  function toGallery() {
    window.open("/gallery", "_blank");
  }

  function cancelTurorial() {
    setTutorialVisible(false);
  }

  const getUploadText = (inputType: string, key: string): string => {
    const targetJobType = selectedJobType.split("-")[0] as JobTypeEnum;
    /* eslint-disable */
    // @ts-ignore: Unreachable code error
    return t(
      `homePage.placeholder.${targetJobType}.${inputType}.${key}` as any,
    );
    /* eslint-enable */
  };

  useEffect(() => {
    formRef.current?.setValue("job_type", selectedJobType);

    let activeJobType: string | undefined = undefined;
    let activeBinderType: string | undefined = undefined;

    if (selectedJobType.includes("-")) {
      const [jType, bType] = selectedJobType.split("-");
      activeJobType = jType;
      activeBinderType = bType;
    } else {
      activeJobType = selectedJobType;
    }

    if (activeJobType) {
      getExamples(activeJobType as JobTypeEnum, activeBinderType);
      dispatch(
        setSelectedMenu({
          job_type: activeJobType as JobTypeEnum,
        }),
      );
    }
  }, [selectedJobType]);

  useEffect(() => {
    if (jobId) {
      onGetJobDetail(jobId);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobType) {
      setSelectedJobType(jobType);
    } else {
      setSelectedJobType("antibody_design_lite-vhh" as JobTypeEnum);
    }
  }, [jobType]);

  useLayoutEffect(() => {
    let resizeObserver: ResizeObserver | undefined = undefined;
    if (divRef.current) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { height } = entry.contentRect; // Get the new height of the element
          setContentHeight(height);
        }
      });

      // Start observing the target div
      resizeObserver.observe(divRef.current);
    }

    return () => {
      if (resizeObserver && divRef.current) {
        resizeObserver.unobserve(divRef.current);
      }
    };
  }, []);

  const binderType = selectedJobType.split("-")[1];

  return (
    <div className={styles.pageContainer}>
      <div
        className={styles.content}
        style={{
          maxWidth:
            selectedJobType === JobTypeEnum.AbAgDocking ||
            (selectedJobType === JobTypeEnum.ProteinBinderDesign &&
              isBinderDesignAdvanced)
              ? "1200px"
              : "840px",
          paddingTop: contentHeight > 500 ? "24px" : "180px",
        }}
      >
        <div ref={divRef}>
          <Form
            onValueChange={onFormValuesChange}
            onSubmit={onSubmit}
            getFormApi={(formApi) => (formRef.current = formApi)}
          >
            {({ formState }: { formState: FormState<JobPayloadModel> }) => (
              <>
                <div className={`${styles.title} appear-animate`}>
                  {t("homePage.title")}
                </div>
                <div className={styles.btnContainer}>
                  <div className="appear-animate">
                    {/* <JobTypeSelect
                      field="job_type"
                      noLabel
                      style={{
                        // width: "183px",
                        border: "none",
                      }}
                      onChange={onJobTypeChange}
                      fieldStyle={{ padding: 0 }}
                      initValue={JobTypeEnum.VhhDesign}
                    /> */}

                    <JobTypeCascader
                      field="job_type"
                      style={{
                        border: "none",
                      }}
                      fieldStyle={{ padding: 0 }}
                      onChange={onJobTypeChange}
                      noLabel
                      initValue="antibody_design_lite-vhh"
                    />
                  </div>
                  <div>
                    {exampleList.map((item, i) => {
                      return (
                        <Button
                          key={item.id}
                          className="default-btn appear-animate"
                          theme="borderless"
                          type="tertiary"
                          size="small"
                          style={{ marginLeft: i !== 0 ? 16 : 0 }}
                          onClick={() => exampleClick(item)}
                        >
                          {item.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {formState.values?.job_type === JobTypeEnum.AbAgDocking ? (
                  <>
                    <DockingTableField
                      ref={dockingTableRef}
                      exampleJobs={exampleJobs}
                      field="docking_attachment"
                      noLabel
                      fieldStyle={{ padding: 0, overflow: "visible" }}
                    />
                    <div className={styles.noticeWrapper}>
                      {t("homePage.footer.dockingText")}{" "}
                      <span className={styles.advanceBtn} onClick={toPromotion}>
                        {t("homePage.footer.dockingAdvanced")}
                      </span>
                      .
                    </div>
                  </>
                ) : isBinderDesignAdvanced ? (
                  <ProteinBinderDesignTableField
                    exampleJobs={exampleJobs}
                    switchSimpleMode={onSwitchSimpleMode}
                    field="advanced_protein_binder_attachment"
                    noLabel
                    fieldStyle={{ padding: 0, overflow: "visible" }}
                  />
                ) : (
                  <>
                    <div className={`${styles.inputSection} appear-animate`}>
                      <div className={styles.inputWrapper}>
                        {formState.values?.vhh_design_attachment
                          ?.protein_type ===
                        VhhDesignTypeEnum.UploadStructure ? (
                          <Upload
                            ref={uploadRef}
                            showUploadList={false}
                            customRequest={() => {}}
                            onAcceptInvalid={onAcceptInvalid}
                            draggable={true}
                            accept=".cif,.pdb"
                            className={styles.uploadContainer}
                            limit={1}
                            dragMainText={getUploadText(
                              formState.values.vhh_design_attachment
                                .protein_type,
                              "text",
                            )}
                            dragSubText={getUploadText(
                              formState.values.vhh_design_attachment
                                .protein_type,
                              "subtext",
                            )}
                            onFileChange={onFileChange}
                          ></Upload>
                        ) : (
                          <MainTextarea
                            noLabel
                            field="input_text"
                            select_type={
                              formState.values?.vhh_design_attachment
                                ?.protein_type ||
                              formState.values?.protein_binder_attachment
                                ?.target_type
                            }
                            job_type={selectedJobType}
                            fieldStyle={{ padding: 0 }}
                            submit={onTextSubmit}
                          />
                        )}

                        {formState.values?.job_type ===
                        JobTypeEnum.ProteinBinderDesign ? (
                          <ProteinBinderDesignAttachmentField
                            fieldStyle={{ padding: 0 }}
                            noLabel
                            field="protein_binder_attachment"
                          />
                        ) : (
                          <VhhDesignAttachmentField
                            fieldStyle={{ padding: 0 }}
                            noLabel
                            field="vhh_design_attachment"
                            onChange={onVhhFiledChange}
                          />
                        )}
                      </div>
                      {formState.values?.vhh_design_attachment?.protein_type !==
                        VhhDesignTypeEnum.UploadStructure && (
                        <div className={styles.searchWrapper}>
                          {formState.values?.job_type ===
                            JobTypeEnum.ProteinBinderDesign && (
                            <ProteinBinderAdvancedField
                              field="protein_advanced"
                              noLabel
                              fieldStyle={{
                                padding: 0,
                                marginRight: "8px",
                                lineHeight: "24px",
                              }}
                              tryAdvanced={onTryAdvanced}
                            />
                          )}

                          <Button
                            theme="solid"
                            type="primary"
                            size="small"
                            htmlType="submit"
                            className={styles.searchBtn}
                            loading={loading}
                            icon={<IconSearch />}
                          ></Button>
                        </div>
                      )}

                      {tutorialVisible && (
                        <>
                          <div className={styles.tuitionContainer}>
                            <ArrowIcon className={styles.arrowIcon} />
                            <div
                              dangerouslySetInnerHTML={{
                                __html: t("homePage.tuition.message", {
                                  itemName:
                                    ANTIBODY_DESIGN_TYPE_MAP[
                                      binderType as AntibodyDesignBinderTypeEnum
                                    ] ||
                                    getPepideLabel(
                                      binderType as PeptideDesignBinderTypeEnum,
                                    ).toLowerCase() +
                                      (i18n.resolvedLanguage === "en"
                                        ? " peptide"
                                        : ""),
                                  targetName: extraInfo
                                    ? extraInfo.fe_target_name
                                    : "",
                                  boldText: styles.boldText,
                                  inputText: formState.values?.input_text || "",
                                }),
                              }}
                            ></div>
                            <div className={styles.footer}>
                              <div>1/4</div>
                              <div>
                                <span className={styles.skip} onClick={onSkip}>
                                  {t("homePage.tuition.skip")}
                                </span>
                                <span className={styles.next} onClick={onNext}>
                                  {t("homePage.tuition.next")}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className={styles.inputMask}></div>
                        </>
                      )}
                    </div>
                    <div className={styles.noticeWrapper}>
                      {t("homePage.footer.denovoText")}{" "}
                      <span className={styles.advanceBtn} onClick={toGallery}>
                        {t("header.gallery")}
                      </span>{" "}
                      !
                    </div>
                  </>
                )}
              </>
            )}
          </Form>
        </div>
      </div>
      <ContactFooter />

      {loginVisible && (
        <JobLoginDialog
          cancel={onLoginCancel}
          submit={onLoginSubmit}
          logined={onLogined}
          loading={loadingInfo}
          type={userType}
          tokenInfo={tokenInfo}
          username={username}
          submitingJob={submitingJob}
          // type="anonymous"
        />
      )}

      {tutorialVisible && (
        <div className={styles.mask} onClick={cancelTurorial}></div>
      )}
    </div>
  );
};

export default Home;
